/**
 * OnboardingProgressWidget — checklist de progression GLOBALE et persistante
 * (Phase 1.4).
 *
 * Pourquoi un nouveau composant plutôt que réutiliser `OnboardingChecklist` ?
 * ──────────────────────────────────────────────────────────────────────────
 * `OnboardingChecklist` est couplé au formulaire de POSITIONNEMENT (il suit les
 * 6 champs de `OnboardingData` : usageType, identity, purpose, tone, values,
 * audience) et n'est monté que dans PositioningTab. Il ne connaît pas les 4
 * étapes macro du setup (positionnement / thématiques / sources / canaux).
 *
 * Ce widget-ci suit ces 4 étapes macro via la source de vérité partagée
 * `useOnboardingProgress` (= `useSetupWizard`), reste visible sur TOUTES les
 * pages tant que le setup est incomplet, et propose une reprise « Vous en étiez
 * à l'étape X » avec un CTA qui rouvre l'onglet concerné. On réutilise le
 * langage visuel de `OnboardingChecklist` (icônes Check / Loader2 / Circle,
 * barre de progression) pour la cohérence.
 *
 * Tailwind figé (V4-FRONT-TAILWIND-FROZEN-CSS) → styles inline pour tout ce qui
 * touche au layout / positionnement. Rendu via portal comme la pastille de
 * génération (DashboardPage) pour échapper aux stacking contexts.
 */
import { useState, useRef, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, Circle, Loader2, ChevronDown, ArrowRight, ListChecks, GripVertical } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  useOnboardingProgress,
  OnboardingStep,
} from '../../hooks/useOnboardingProgress';
import type { SetupWizardData } from '../../hooks/useSetupWizard';
import { SIDEBAR_ITEMS } from '../../utils/Constants';
import { useAuth } from '../../hooks/useAuth';
import { useOnboardingPreference } from '../../hooks/useOnboardingPreference';

interface OnboardingProgressWidgetProps extends SetupWizardData {
  onOpenSidebarTab: (tabId: string) => void;
  /**
   * V4-FRONT-WIZARD-REACCESS 2026-07-11 — parité mobile. Sur mobile, le widget
   * s'ancre au-dessus de la MobileBottomBar (pas de rail Analytics à droite) et
   * démarre replié pour ne pas masquer le contenu.
   */
  isMobile?: boolean;
}

// Ordre canonique des 4 étapes macro (identique à la cascade de useOnboardingProgress).
const STEP_ORDER: OnboardingStep[] = [
  OnboardingStep.POSITIONING,
  OnboardingStep.TOPICS,
  OnboardingStep.SOURCES,
  OnboardingStep.CHANNELS,
];

const STEP_TAB: Record<string, string> = {
  [OnboardingStep.POSITIONING]: SIDEBAR_ITEMS.POSITIONING,
  [OnboardingStep.TOPICS]: SIDEBAR_ITEMS.WATCH,
  [OnboardingStep.SOURCES]: SIDEBAR_ITEMS.SOURCES,
  [OnboardingStep.CHANNELS]: SIDEBAR_ITEMS.CHANNELS,
};

const SESSION_KEY = 'raiso_onboarding_widget_collapsed';
// V4-ONBOARDING-WIDGET-DRAGGABLE — position persistée (localStorage) + défaut à
// droite, à côté du rail Analytics (QueueSidebar, w-16 fixed right-0).
const POS_KEY = 'raiso_onboarding_widget_pos';
// V4-FRONT-WIZARD-WIDGET-REOPEN — position de drag séparée desktop/mobile.
// Avant : clé unique partagée → un placement mémorisé sur desktop était réutilisé
// tel quel sur mobile (clampé donc jamais hors-écran, mais placement sous-optimal
// au 1er affichage avant re-drag).
const posKey = (isMobile: boolean) => `${POS_KEY}_${isMobile ? 'mobile' : 'desktop'}`;
const WIDGET_WIDTH = 288;
const RIGHT_RAIL_CLEARANCE = 88; // 64px rail QueueSidebar + ~24px de marge
// V4-FRONT-WIZARD-REACCESS 2026-07-11 — hauteur réservée à la MobileBottomBar
// (fixed bottom-0, ~96px avec safe-area) pour ne pas ancrer le widget dessous.
const MOBILE_BOTTOM_CLEARANCE = 112;
const MOBILE_PILL_APPROX = 190; // largeur approx. de la pastille repliée sur mobile

type WidgetPos = { top: number; left: number };

// Position par défaut. Desktop : en haut à droite, juste à gauche du rail Analytics.
// Mobile : en bas à droite, au-dessus de la MobileBottomBar (pas de rail droit).
function defaultPos(isMobile = false): WidgetPos {
  if (typeof window === 'undefined') return { top: 100, left: 800 };
  if (isMobile) {
    return {
      top: Math.max(24, window.innerHeight - MOBILE_BOTTOM_CLEARANCE - 44),
      left: Math.max(12, window.innerWidth - MOBILE_PILL_APPROX - 12),
    };
  }
  return { top: 100, left: Math.max(24, window.innerWidth - WIDGET_WIDTH - RIGHT_RAIL_CLEARANCE) };
}

// Clamp dans le viewport (évite qu'une position mémorisée sorte de l'écran après resize).
// Sur mobile, on garde une marge basse pour la MobileBottomBar.
function clampPos(p: WidgetPos, isMobile = false): WidgetPos {
  if (typeof window === 'undefined') return p;
  const bottomLimit = isMobile
    ? window.innerHeight - MOBILE_BOTTOM_CLEARANCE
    : window.innerHeight - 60;
  return {
    top: Math.min(Math.max(8, p.top), Math.max(8, bottomLimit)),
    left: Math.min(Math.max(8, p.left), window.innerWidth - 60),
  };
}

function readInitialPos(isMobile = false): WidgetPos {
  try {
    const raw = localStorage.getItem(posKey(isMobile));
    if (raw) {
      const p = JSON.parse(raw);
      if (typeof p?.top === 'number' && typeof p?.left === 'number') return clampPos(p, isMobile);
    }
  } catch {
    // localStorage indisponible / JSON corrompu → défaut
  }
  return defaultPos(isMobile);
}

export function OnboardingProgressWidget({
  onOpenSidebarTab,
  isMobile = false,
  ...data
}: OnboardingProgressWidgetProps) {
  const { t } = useLanguage();
  const onboarding = useOnboardingProgress(data);
  // V4-FRONT-WIZARD-WIDGET-REOPEN — rouvrir le SetupWizard (masqué via la
  // préférence par-space) directement depuis le widget, sans passer par Settings.
  const { currentSpace } = useAuth();
  const { setVisible: setWizardVisible } = useOnboardingPreference(currentSpace?.id);
  // Sur mobile : replié par défaut (moins intrusif) sauf si explicitement déplié
  // cette session. Sur desktop : déplié par défaut sauf si replié cette session.
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    isMobile
      ? sessionStorage.getItem(SESSION_KEY) !== '0'
      : sessionStorage.getItem(SESSION_KEY) === '1',
  );

  // V4-ONBOARDING-WIDGET-DRAGGABLE — position déplaçable, mémorisée en localStorage.
  const [pos, setPos] = useState<WidgetPos>(() => readInitialPos(isMobile));
  // Marque un drag venant de se terminer pour ne PAS déclencher le toggle (le
  // header sert à la fois de poignée de déplacement et de bouton replier).
  const justDraggedRef = useRef(false);

  const startDrag = (e: ReactPointerEvent) => {
    // Ignore le clic droit / molette.
    if (e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startTop = pos.top;
    const startLeft = pos.left;
    let moved = false;
    let current: WidgetPos = { top: startTop, left: startLeft };

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved = true;
      current = clampPos({ top: startTop + dy, left: startLeft + dx }, isMobile);
      setPos(current);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      justDraggedRef.current = moved;
      if (moved) {
        try {
          localStorage.setItem(posKey(isMobile), JSON.stringify(current));
        } catch {
          // localStorage indisponible → position non persistée (non bloquant)
        }
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const toggle = () => {
    // Un déplacement vient de se terminer → on avale le clic, pas de repli.
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return;
    }
    setCollapsed((prev) => {
      const next = !prev;
      sessionStorage.setItem(SESSION_KEY, next ? '1' : '0');
      return next;
    });
  };

  // Ne s'affiche jamais pour un lecteur (guest / viewer d'agence) ni une fois le
  // setup terminé — la source de vérité est `useOnboardingProgress`.
  if (onboarding.isReadOnly || onboarding.isOnboardingComplete) return null;

  const sp = t.onboarding?.setupProgress ?? {
    title: 'Votre configuration',
    resumePrefix: 'Reprenez à',
    continueBtn: 'Continuer',
    expand: 'Afficher la progression',
    stepPositioning: 'Positionnement',
    stepTopics: 'Thématiques',
    stepSources: 'Sources',
    stepChannels: 'Canaux',
    optional: 'optionnel',
    reopenAssistant: "Rouvrir l'assistant",
    dragHint: 'Glissez pour déplacer',
  };

  const STEP_LABEL: Record<string, string> = {
    [OnboardingStep.POSITIONING]: sp.stepPositioning,
    [OnboardingStep.TOPICS]: sp.stepTopics,
    [OnboardingStep.SOURCES]: sp.stepSources,
    [OnboardingStep.CHANNELS]: sp.stepChannels,
  };

  const currentIndex = STEP_ORDER.indexOf(onboarding.currentStep);
  // currentIndex === -1 signifie COMPLETE, déjà filtré plus haut.
  const completedCount = currentIndex;

  // V4-ONBOARDING-CHECKLIST-OPTIONAL — veille désactivée ⇒ Thématiques + Sources
  // sont OPTIONNELLES : on les affiche « (optionnel) » plutôt que « fait » quand
  // l'utilisateur ne les a pas réellement configurées.
  const watchOptional = data.sectorWatchEnabled === false;
  const topicsDone = (data.watchTopics?.length ?? 0) > 0;
  const sourcesDone = (data.sources?.length ?? 0) > 0;
  const isOptionalStep = (step: OnboardingStep, genuinelyDone: boolean) =>
    watchOptional && (step === OnboardingStep.TOPICS || step === OnboardingStep.SOURCES) && !genuinelyDone;
  const currentLabel = STEP_LABEL[onboarding.currentStep] ?? '';
  const currentTab = STEP_TAB[onboarding.currentStep];

  const containerStyle: CSSProperties = {
    position: 'fixed',
    top: pos.top,
    left: pos.left,
    zIndex: 21000,
    backgroundColor: 'var(--color-surface-elevated, var(--color-surface))',
    border: '1px solid var(--color-border)',
    borderRadius: 14,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
    // Sur mobile, borner la largeur du panneau déplié au viewport (marges 12px × 2).
    width: collapsed
      ? undefined
      : isMobile && typeof window !== 'undefined'
        ? Math.min(288, window.innerWidth - 24)
        : 288,
    overflow: 'hidden',
  };

  // Vue repliée : petite pastille cliquable avec le compteur.
  if (collapsed) {
    return createPortal(
      <button
        type="button"
        onClick={toggle}
        onPointerDown={startDrag}
        title={`${sp.expand} — ${sp.dragHint}`}
        className="flex items-center gap-2"
        style={{ ...containerStyle, padding: '10px 14px', cursor: 'grab', touchAction: 'none' }}
      >
        <ListChecks className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
          {sp.title}
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded-full font-semibold"
          style={{ backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}
        >
          {completedCount}/{STEP_ORDER.length}
        </span>
        {/* V4-ONBOARDING-WIDGET-BLOCKS-CLICKS — poignée visible : rien n'indiquait
            que ce widget est déplaçable avant qu'il n'intercepte les clics du
            contenu sous-jacent (ex. chat de l'assistant positionnement). */}
        <GripVertical className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
      </button>,
      document.body,
    );
  }

  return createPortal(
    <div style={containerStyle}>
      {/* Header cliquable — replie le widget */}
      <button
        type="button"
        onClick={toggle}
        onPointerDown={startDrag}
        title={`${sp.title} — ${sp.dragHint}`}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
        style={{ cursor: 'grab', touchAction: 'none' }}
      >
        <ListChecks className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
        <span className="text-sm font-semibold flex-1" style={{ color: 'var(--color-text-primary)' }}>
          {sp.title}
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded-full font-semibold"
          style={{ backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}
        >
          {completedCount}/{STEP_ORDER.length}
        </span>
        {/* V4-ONBOARDING-WIDGET-BLOCKS-CLICKS — même poignée visible que la pastille repliée. */}
        <GripVertical className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
        <ChevronDown
          className="w-4 h-4 transition-transform"
          style={{ color: 'var(--color-text-secondary)', transform: 'rotate(180deg)' }}
        />
      </button>

      {/* Barre de progression */}
      <div className="h-0.5 mx-3 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${(completedCount / STEP_ORDER.length) * 100}%`,
            backgroundColor: 'var(--color-primary)',
          }}
        />
      </div>

      {/* Items */}
      <div className="px-3 py-3 space-y-2">
        {STEP_ORDER.map((step, idx) => {
          const completed = idx < currentIndex;
          const current = idx === currentIndex;
          const genuinelyDone = step === OnboardingStep.TOPICS ? topicsDone
            : step === OnboardingStep.SOURCES ? sourcesDone : completed;
          const optional = isOptionalStep(step, genuinelyDone);
          return (
            <div key={step} className="flex items-center gap-2">
              {optional ? (
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center border flex-shrink-0"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <Circle className="w-3 h-3" style={{ color: 'var(--color-text-muted)', opacity: 0.5 }} />
                </div>
              ) : completed ? (
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--color-success-light)' }}
                >
                  <Check className="w-3 h-3" style={{ color: 'var(--color-success)' }} />
                </div>
              ) : current ? (
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--color-primary-light)' }}
                >
                  <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--color-primary)' }} />
                </div>
              ) : (
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center border flex-shrink-0"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <Circle className="w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />
                </div>
              )}
              <span
                className="text-xs flex-1"
                style={{
                  color: completed && !optional ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  fontWeight: current ? 600 : 400,
                }}
              >
                {STEP_LABEL[step]}
                {optional && (
                  <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                    {' · '}{sp.optional ?? 'optionnel'}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {/* Reprise « Vous en étiez à l'étape X » + CTA */}
      {currentTab && (
        <div className="px-3 pb-3 pt-1" style={{ borderTop: '1px solid var(--color-border)' }}>
          <p className="text-xs mb-2 mt-2" style={{ color: 'var(--color-text-secondary)' }}>
            {sp.resumePrefix} : <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{currentLabel}</span>
          </p>
          <button
            type="button"
            onClick={() => onOpenSidebarTab(currentTab)}
            className="w-full flex items-center justify-center gap-2 rounded-full cursor-pointer"
            style={{
              padding: '8px 12px',
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-text-light)',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {sp.continueBtn}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
          {/* V4-FRONT-WIZARD-WIDGET-REOPEN — lien direct pour rouvrir l'assistant
              (SetupWizard), sans passer par le toggle Settings. */}
          <button
            type="button"
            data-testid="onboarding-widget-reopen-assistant"
            onClick={() => setWizardVisible(true)}
            className="w-full text-center cursor-pointer"
            style={{
              marginTop: 8,
              padding: 4,
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: 12,
              textDecoration: 'underline',
            }}
          >
            {sp.reopenAssistant}
          </button>
        </div>
      )}
    </div>,
    document.body,
  );
}
